import type { UseComboboxPropGetters } from "downshift";
import { useCombobox, useMultipleSelection } from "downshift";
import type { ReactNode } from "react";
import { memo, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { twJoin } from "tailwind-merge";

import chevronDownIcon from "@/assets/icons/chevron-down.svg";
import xCloseIcon from "@/assets/icons/x-close.svg";
import { Checkbox } from "@/design-system/Checkbox";
import { Tooltip } from "@/design-system/Tooltip";
import { autoCompleteDisabledProperties } from "@/helpers/auto-complete-helpers";
import { twResolve } from "@/helpers/tw-resolve";
import { useSelectFloating } from "@/ui-adapters/useSelectFloating";

import { fieldControlStateVariants } from "./field-control";

export interface CheckboxMultiSelectProps<T> {
  placeholder: string;
  selected: T[] | undefined;
  disabled?: boolean;
  items: readonly T[];
  renderOption: (item: T, selected: boolean) => ReactNode;
  renderMoreSelected: (count: number) => ReactNode;
  keySelector: (item: T) => string | number;
  onChange?: (items: T[]) => void;
  onBlur?: () => void;
  disabledItems?: T[];
  disabledItemTooltip?: string;
  disabledItemsStillSelectable?: boolean;
  "aria-invalid"?: boolean;
  "aria-label"?: string;
  "data-testid"?: string;
  id?: string;
  allSelectedText?: ReactNode;
  hideClearButton?: boolean;
  usePortal?: boolean;
  searchableFieldSelector?: (item: T) => string[];
  searchPlaceholder?: string;
  /** Runs when the search input changes and receives an empty string when the input is cleared or an item is selected. */
  onSearchChange?: (value: string) => void;
}

/** Renders a typed multi-value select with checkbox options and a compact selection summary. */
export function CheckboxMultiSelect<T>({
  selected = [],
  disabled,
  placeholder,
  items,
  renderOption,
  renderMoreSelected,
  keySelector,
  onChange,
  onBlur,
  disabledItems = [],
  disabledItemTooltip,
  disabledItemsStillSelectable,
  allSelectedText,
  hideClearButton,
  usePortal = false,
  searchableFieldSelector,
  searchPlaceholder,
  onSearchChange,
  "aria-invalid": isInvalid,
  "aria-label": ariaLabel,
  "data-testid": testId,
  id,
}: CheckboxMultiSelectProps<T>): React.ReactNode {
  const [inputValue, setInputValue] = useState("");

  const { getDropdownProps, setSelectedItems, addSelectedItem, removeSelectedItem, selectedItems } =
    useMultipleSelection({
      selectedItems: selected,
      defaultActiveIndex: 0,
      onSelectedItemsChange: (stateChange) => {
        onChange?.(stateChange.selectedItems ?? []);
      },
      onStateChange: ({ type, selectedItems: newSelectedItems = [] }) => {
        switch (type) {
          case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownBackspace:
          case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
          case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
          case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
            setSelectedItems(newSelectedItems);
            break;
          default:
            break;
        }
      },
    });

  const filteredItems = useMemo(() => {
    const baseFiltered = inputValue
      ? items.filter((item) => {
          if (searchableFieldSelector) {
            return searchableFieldSelector(item).some((field) =>
              field.toLowerCase().includes(inputValue.toLowerCase()),
            );
          }

          const renderedResult = renderOption(item, false);
          if (typeof renderedResult === "string") {
            return renderedResult.toLowerCase().includes(inputValue.toLowerCase());
          }

          return false;
        })
      : items;

    const selectedNotInFiltered = selectedItems.filter(
      (selected) => !baseFiltered.some((item) => keySelector(item) === keySelector(selected)),
    );

    return [...baseFiltered, ...selectedNotInFiltered];
  }, [items, searchableFieldSelector, inputValue, renderOption, selectedItems, keySelector]);

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    getLabelProps,
    highlightedIndex,
    getItemProps,
    closeMenu,
  } = useCombobox({
    inputValue,
    defaultHighlightedIndex: 0,
    selectedItem: null,
    items: filteredItems,
    stateReducer: (state, { type, changes }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            ...(changes.selectedItem
              ? {
                  isOpen: true,
                  highlightedIndex:
                    filteredItems.length <= state.highlightedIndex + 1
                      ? filteredItems.length - 2
                      : state.highlightedIndex,
                }
              : null),
          };
        default:
          return changes;
      }
    },
    onStateChange: ({ inputValue: newInputValue, type, selectedItem }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputChange:
          setInputValue(newInputValue || "");
          onSearchChange?.(newInputValue || "");
          break;
        case useCombobox.stateChangeTypes.InputBlur:
          setInputValue("");
          onSearchChange?.("");
          onBlur?.();
          break;
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (selectedItem && (disabledItemsStillSelectable || !disabledItems.includes(selectedItem))) {
            setInputValue("");
            onSearchChange?.("");
            if (selectedItems.includes(selectedItem)) {
              removeSelectedItem(selectedItem);
            } else {
              addSelectedItem(selectedItem);
            }
          }
          break;
        default:
          break;
      }
    },
  });

  const { referenceRef, menuRef, menuProps, opensAbove } = useSelectFloating({
    open: isOpen,
    usePortal,
    menuSize: "checkbox-multi-select",
  });

  const menuContent = isOpen
    ? filteredItems.map((item, index) => (
        <ListItem<T>
          key={keySelector(item)}
          renderOption={renderOption}
          selectedItems={selectedItems}
          item={item}
          index={index}
          isHighlighted={highlightedIndex === index}
          getItemProps={getItemProps}
          disabled={disabledItems.includes(item)}
          disabledTooltip={disabledItemTooltip}
          disabledStillSelectable={disabledItemsStillSelectable}
        />
      ))
    : null;

  const selectedAnyItems = selectedItems.length > 0;
  const selectedAll = selectedItems.length === items.length;
  const displayAllSelectedText = selectedAll && allSelectedText;
  const isClearButtonVisible = !hideClearButton && selectedAnyItems && !isOpen && !disabled;

  const menu = (
    <ul
      {...getMenuProps({ ref: menuRef }, { suppressRefError: !isOpen })}
      {...menuProps}
      data-portal-dropdown={usePortal || undefined}
      className={twJoin(
        "z-select-menu flex w-max cursor-default flex-col items-start overflow-auto rounded-lg border border-border-brand-secondary bg-background-default shadow-overlay outline-hidden!",
        opensAbove ? "rounded-b-none" : "rounded-t-none",
      )}
    >
      {menuContent}
    </ul>
  );

  return (
    <div
      className={twResolve("relative", isOpen && "z-20", disabled && "pointer-events-none")}
      data-testid={testId ?? "checkbox-multi-select"}
    >
      <div>
        <div
          {...getLabelProps()}
          ref={referenceRef}
          className={twResolve(
            "relative flex h-control-md w-full cursor-default items-center overflow-hidden rounded-lg border border-border-secondary bg-background-default pl-select-trigger-inline text-left leading-select-trigger",
            isOpen && "border-border-brand-secondary",
            isOpen && (opensAbove ? "rounded-t-none" : "rounded-b-none"),
            fieldControlStateVariants({ invalid: isInvalid, focus: "within" }),
            isClearButtonVisible ? "pr-checkbox-multi-select-clear" : "pr-searchable-select-adornment",
            disabled && "bg-background-disabled text-text-on-disabled",
          )}
        >
          {!selectedAnyItems && !isOpen && (
            <span className="pointer-events-none absolute right-searchable-select-adornment left-select-trigger-inline truncate text-text-secondary">
              {placeholder}
            </span>
          )}
          {selectedAnyItems && !isOpen && (
            <span
              className={twJoin(
                "pointer-events-none absolute left-select-trigger-inline flex items-center overflow-hidden",
                isClearButtonVisible ? "right-checkbox-multi-select-clear" : "right-searchable-select-adornment",
              )}
            >
              {displayAllSelectedText && <span className="text-text-primary">{allSelectedText}</span>}
              {!displayAllSelectedText && selectedItems.length > 0 && (
                <>
                  <span className="truncate">{renderOption(selectedItems[0], false)}</span>
                  {selectedItems.length > 1 && (
                    <span className="text-body-emphasis whitespace-nowrap">
                      , {renderMoreSelected(selectedItems.length - 1)}
                    </span>
                  )}
                </>
              )}
            </span>
          )}
          <input
            {...getInputProps(
              getDropdownProps({
                preventKeyAction: isOpen,
              }),
            )}
            placeholder={isOpen ? searchPlaceholder || placeholder : undefined}
            className={twJoin(
              "h-control-md w-full flex-1 py-select-option-block text-body placeholder:text-text-tertiary focus:outline-hidden",
              !isOpen && "cursor-default text-transparent caret-transparent",
            )}
            disabled={disabled}
            aria-invalid={isInvalid}
            aria-label={ariaLabel}
            {...autoCompleteDisabledProperties}
          />
          {isClearButtonVisible && (
            <button
              type="button"
              className="absolute top-0 right-searchable-select-adornment z-10 flex h-full items-center justify-center focus:outline-hidden focus-visible:ring-1 focus-visible:ring-border-tertiary focus-visible:ring-inset"
              aria-label="clear button"
              data-testid="checkbox-multi-select-clear"
              onClick={(event) => {
                event.stopPropagation();
                setSelectedItems([]);
                closeMenu();
              }}
              onKeyDown={(event) => {
                event.stopPropagation();
                if (event.key === "Enter") {
                  setSelectedItems([]);
                  closeMenu();
                }
              }}
            >
              <span className="flex items-center text-text-secondary">
                <CheckboxMultiSelectIcon name={xCloseIcon} />
              </span>
            </button>
          )}
          <button
            {...getToggleButtonProps({
              type: "button",
            })}
            id={id}
            className="absolute top-0 right-0 flex h-full w-searchable-select-adornment items-center justify-center focus:outline-hidden"
            aria-label="toggle menu"
          >
            <span className={twResolve("flex items-center text-text-secondary", disabled && "text-text-on-disabled")}>
              <CheckboxMultiSelectIcon name={chevronDownIcon} animateRotation rotate={isOpen} />
            </span>
          </button>
        </div>
      </div>
      {isOpen && (usePortal ? createPortal(menu, document.body) : menu)}
    </div>
  );
}

function CheckboxMultiSelectIcon({
  name,
  animateRotation = false,
  rotate = false,
}: {
  name: string;
  animateRotation?: boolean;
  rotate?: boolean;
}): React.ReactNode {
  return (
    <span
      className={twJoin("shrink-0 fill-transparent", animateRotation && "transition-transform", rotate && "rotate-180")}
    >
      <svg aria-hidden="true" width={16} height={16} data-testid={name}>
        <use xlinkHref={`#${name}`} />
      </svg>
    </span>
  );
}

const ListItem = memo(ListItemInner) as typeof ListItemInner;
function ListItemInner<T>(props: {
  item: T;
  index: number;
  isHighlighted: boolean;
  getItemProps: UseComboboxPropGetters<T>["getItemProps"];
  renderOption: CheckboxMultiSelectProps<T>["renderOption"];
  selectedItems: T[];
  disabled: boolean;
  disabledTooltip?: string;
  disabledStillSelectable?: boolean;
}) {
  const {
    item,
    index,
    isHighlighted,
    getItemProps,
    renderOption,
    selectedItems,
    disabled,
    disabledTooltip,
    disabledStillSelectable,
  } = props;
  const isSelected = selectedItems.includes(item);

  return (
    <li
      data-testid="checkbox-multi-select-item"
      {...getItemProps({
        index,
        item,
      })}
      className={twJoin(
        "w-full cursor-pointer px-select-option-inline py-select-option-block select-none first:mt-select-menu-edge last:mb-select-menu-edge",
        isHighlighted && "bg-background-brand-light",
        disabled && !disabledStillSelectable && "cursor-default",
        disabled && "text-text-disabled",
        !disabled && "text-text-primary",
      )}
    >
      <Tooltip tooltip={disabled && disabledTooltip}>
        <div className={twJoin("flex items-center gap-space-sm text-body", isSelected && "text-body-emphasis")}>
          <Checkbox checked={isSelected} disabled={disabled && !disabledStillSelectable} onChange={() => null} />
          {renderOption(item, selectedItems.includes(item))}
        </div>
      </Tooltip>
    </li>
  );
}
